import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
   Badge,
   Button,
   Checkbox,
   Flex,
   Loader,
   Modal,
   ScrollArea,
   Table,
   Text,
   TextInput,
} from "@mantine/core";
import AuthContext from "../../contexts/AuthContext";
import showNotification from "../../utils/showNotification";

const getHeaders = (authToken) => ({
   "Content-Type": "application/json",
   Authorization: "Bearer " + authToken,
});

/**
 * Checklist of the students of an academic unit.
 * The admin selects which students are enabled to receive (and download) the
 * certificate. Students that already have a certificate are shown as issued,
 * and the ones without it can be issued at any time.
 */
const CertificateChecklistModal = ({
   opened,
   onClose,
   academicUnitId,
   academicUnitName,
   onIssued,
}) => {
   const { authToken } = useContext(AuthContext);
   const [students, setStudents] = useState([]);
   const [missingStudents, setMissingStudents] = useState([]);
   const [selected, setSelected] = useState(new Set());
   const [search, setSearch] = useState("");
   const [loading, setLoading] = useState(false);
   const [submitting, setSubmitting] = useState(false);

   useEffect(() => {
      if (!opened || !academicUnitId) return;
      const fetchStudents = async () => {
         setLoading(true);
         try {
            const response = await axios.get(
               `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/acaUnitStudents/${academicUnitId}`,
               { headers: getHeaders(authToken) }
            );
            setStudents(response.data.students || []);
            setMissingStudents(response.data.missingStudents || []);
            setSelected(new Set());
            setSearch("");
         } catch (error) {
            showNotification({
               color: "red",
               status: "error",
               title: "Operación fallida.",
               message: `No se pudieron cargar los estudiantes. Detalle: ${
                  error.response?.data?.message || error.message
               }`,
               autoClose: 12000,
            });
         } finally {
            setLoading(false);
         }
      };
      fetchStudents();
   }, [opened, academicUnitId, authToken]);

   const pendingStudents = useMemo(
      () => students.filter((student) => !student.hasCertificate),
      [students]
   );
   const issuedCount = students.length - pendingStudents.length;

   const filteredStudents = useMemo(() => {
      const term = search.trim().toLowerCase();
      if (!term) return students;
      return students.filter((student) =>
         `${student.name} ${student.lastname} ${student.email}`
            .toLowerCase()
            .includes(term)
      );
   }, [students, search]);

   const visiblePending = filteredStudents.filter((s) => !s.hasCertificate);
   const allVisibleSelected =
      visiblePending.length > 0 &&
      visiblePending.every((student) => selected.has(student._id));
   const someVisibleSelected = visiblePending.some((student) =>
      selected.has(student._id)
   );

   const toggleStudent = (id) => {
      setSelected((prev) => {
         const next = new Set(prev);
         next.has(id) ? next.delete(id) : next.add(id);
         return next;
      });
   };

   const toggleAllVisible = () => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (allVisibleSelected) {
            visiblePending.forEach((student) => next.delete(student._id));
         } else {
            visiblePending.forEach((student) => next.add(student._id));
         }
         return next;
      });
   };

   const handleSubmit = async () => {
      if (selected.size === 0) return;
      setSubmitting(true);
      try {
         const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/enableCertificate`,
            { academicUnitId, studentIds: Array.from(selected) },
            { headers: getHeaders(authToken) }
         );
         showNotification({
            color: "green",
            status: "success",
            title: "¡Excelente! Operación exitosa.",
            message:
               response.data?.message ||
               "Los certificados fueron habilitados con éxito.",
         });
         onIssued && onIssued();
         onClose();
      } catch (error) {
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: `No se pudieron habilitar los certificados. Detalle: ${
               error.response?.data?.message || error.message
            }`,
            autoClose: 12000,
         });
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <Modal
         opened={opened}
         onClose={onClose}
         size="xl"
         title={<strong>Habilitar certificados</strong>}
      >
         <Flex direction="column" gap="sm">
            <Text size="sm">
               Unidad académica: <strong>{academicUnitName}</strong>
            </Text>
            <Text size="sm" c="dimmed">
               Seleccioná los estudiantes que están habilitados para recibir el
               certificado (por ejemplo, quienes completaron el pago del curso).
               Los que no selecciones quedarán pendientes y podrás emitirlos más
               adelante.
            </Text>

            {loading ? (
               <Flex justify="center" p="lg">
                  <Loader />
               </Flex>
            ) : (
               <>
                  <Flex gap="xs" wrap="wrap">
                     <Badge color="gray">Estudiantes: {students.length}</Badge>
                     <Badge color="green">Con certificado: {issuedCount}</Badge>
                     <Badge color="orange">
                        Sin certificado: {pendingStudents.length}
                     </Badge>
                     <Badge color="blue">Seleccionados: {selected.size}</Badge>
                  </Flex>

                  {missingStudents.length > 0 && (
                     <Text size="xs" c="red">
                        No se encontraron usuarios para los ids:{" "}
                        {missingStudents.join(", ")}
                     </Text>
                  )}

                  <TextInput
                     placeholder="Buscar por nombre, apellido o email"
                     value={search}
                     onChange={(e) => setSearch(e.currentTarget.value)}
                  />

                  <ScrollArea h={380}>
                     <Table stickyHeader highlightOnHover>
                        <Table.Thead>
                           <Table.Tr>
                              <Table.Th w={40}>
                                 <Checkbox
                                    aria-label="Seleccionar todos los pendientes"
                                    checked={allVisibleSelected}
                                    indeterminate={
                                       someVisibleSelected && !allVisibleSelected
                                    }
                                    disabled={visiblePending.length === 0}
                                    onChange={toggleAllVisible}
                                 />
                              </Table.Th>
                              <Table.Th>Estudiante</Table.Th>
                              <Table.Th>Email</Table.Th>
                              <Table.Th>Certificado</Table.Th>
                           </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                           {filteredStudents.length === 0 ? (
                              <Table.Tr>
                                 <Table.Td colSpan={4}>
                                    <Text ta="center" size="sm" c="dimmed">
                                       No hay estudiantes para mostrar.
                                    </Text>
                                 </Table.Td>
                              </Table.Tr>
                           ) : (
                              filteredStudents.map((student) => (
                                 <Table.Tr key={student._id}>
                                    <Table.Td>
                                       <Checkbox
                                          aria-label={`Habilitar a ${student.name} ${student.lastname}`}
                                          checked={
                                             student.hasCertificate ||
                                             selected.has(student._id)
                                          }
                                          disabled={student.hasCertificate}
                                          onChange={() =>
                                             toggleStudent(student._id)
                                          }
                                       />
                                    </Table.Td>
                                    <Table.Td>
                                       {student.name} {student.lastname}
                                    </Table.Td>
                                    <Table.Td>{student.email}</Table.Td>
                                    <Table.Td>
                                       {student.hasCertificate ? (
                                          <Badge color="green">
                                             Emitido{" "}
                                             {student.dateOfIssue
                                                ? moment(student.dateOfIssue).format(
                                                     "DD/MM/YYYY"
                                                  )
                                                : ""}
                                             {student.downloaded
                                                ? " · descargado"
                                                : ""}
                                          </Badge>
                                       ) : (
                                          <Badge color="orange">Pendiente</Badge>
                                       )}
                                    </Table.Td>
                                 </Table.Tr>
                              ))
                           )}
                        </Table.Tbody>
                     </Table>
                  </ScrollArea>
               </>
            )}

            <Flex justify="flex-end" gap="md" mt="sm">
               <Button variant="subtle" onClick={onClose} disabled={submitting}>
                  Cancelar
               </Button>
               <Button
                  variant="outline"
                  onClick={handleSubmit}
                  disabled={selected.size === 0 || submitting || loading}
               >
                  {submitting ? (
                     <Loader size="sm" />
                  ) : (
                     `Emitir certificados (${selected.size})`
                  )}
               </Button>
            </Flex>
         </Flex>
      </Modal>
   );
};

export default CertificateChecklistModal;
